/**
 * Cutting a module descriptor's `description` down to something a modal can show.
 *
 * The field is engineering documentation written for whoever maintains the
 * extractor — thousands of characters describing selectors and fallbacks, with
 * backticks and `<placeholder>` fragments that read as broken markup to anyone
 * who is not the person who wrote them. The Run scraper modal used to render it
 * whole, which buried the two useful lines (module name, version) under prose
 * nobody in that window needs.
 *
 * So: a first-sentence summary in the modal, the full text behind an expander.
 * Pure and here rather than inline in the component because "where does the
 * first sentence end" is the kind of thing that quietly breaks on a decimal
 * version number or an abbreviation, and that is worth a test.
 */

/** Where the summary is cut when no sentence end is found in range. */
export const SUMMARY_MAX_CHARS = 140;

/** Abbreviations whose trailing period never ends a sentence. */
const ABBREVIATIONS = ['e.g.', 'i.e.', 'etc.', 'vs.', 'approx.', 'cf.'];

/** True when the period at `index` is part of an abbreviation or a number. */
function isFalseSentenceEnd(text: string, index: number): boolean {
  const next = text[index + 1];
  /* "v2.1", "1.5s" — a digit either side means a number, not a full stop. */
  if (next !== undefined && /\d/.test(next) && /\d/.test(text[index - 1] ?? '')) return true;
  const before = text.slice(0, index + 1).toLowerCase();
  return ABBREVIATIONS.some((abbr) => before.endsWith(abbr));
}

/**
 * The first sentence of `description`, or a clipped prefix when the first
 * sentence runs past `maxChars`.
 *
 * A sentence end only counts when whitespace or the end of the text follows it,
 * so "detail.html" and "v2.1" stay intact. Falling past `maxChars` clips at the
 * last word boundary and appends an ellipsis — never mid-word, because a
 * half-word is read as a rendering bug rather than as a truncation.
 */
export function summarizeScraperDescription(
  description: string | null | undefined,
  maxChars: number = SUMMARY_MAX_CHARS,
): string {
  const text = (description ?? '').trim().replace(/\s+/g, ' ');
  if (text === '') return '';

  for (let i = 0; i < text.length && i < maxChars; i += 1) {
    const char = text[i];
    if (char !== '.' && char !== '!' && char !== '?') continue;
    const next = text[i + 1];
    if (next !== undefined && next !== ' ') continue;
    if (char === '.' && isFalseSentenceEnd(text, i)) continue;
    return text.slice(0, i + 1);
  }

  if (text.length <= maxChars) return text;
  const clipped = text.slice(0, maxChars);
  const lastSpace = clipped.lastIndexOf(' ');
  return `${(lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).trimEnd()}…`;
}

/**
 * Whether the expander is worth rendering: only when it would reveal something
 * the summary does not already say.
 */
export function hasMoreThanSummary(
  description: string | null | undefined,
  maxChars: number = SUMMARY_MAX_CHARS,
): boolean {
  const text = (description ?? '').trim();
  if (text === '') return false;
  return text.replace(/\s+/g, ' ') !== summarizeScraperDescription(text, maxChars);
}
